import { signal } from '@preact/signals-core';
import { activeRoleSignal, getAuthHeaders } from './assets';

export const auditLogsSignal = signal<any[]>([]);
export const selectedAuditLogSignal = signal<any | null>(null);
export const auditFiltersSignal = signal({
	action: 'all',
	tableName: 'all',
	limit: 20,
	offset: 0,
});
export const rollbackLoadingSignal = signal<boolean>(false);
export const usersMapSignal = signal<Map<string, string>>(new Map());


export async function refreshAuditLogs() {
	const filters = auditFiltersSignal.value;
	let url = `/api/v1/audit-logs?limit=${filters.limit}&offset=${filters.offset}`;

	if (filters.action && filters.action !== 'all') {
		url += `&action=${filters.action}`;
	}
	if (filters.tableName && filters.tableName !== 'all') {
		url += `&tableName=${filters.tableName}`;
	}

	try {
		// Fetch users list to map actor UUIDs back to human-readable emails
		const uRes = await fetch('/api/v1/users?limit=1000', { headers: getAuthHeaders() });
		if (uRes.ok) {
			const users = await uRes.json();
			const m = new Map<string, string>();
			users.forEach((u: any) => {
				m.set(u.id, u.email);
			});
			usersMapSignal.value = m;
		}

		const res = await fetch(url, { headers: getAuthHeaders() });
		if (res.ok) {
			auditLogsSignal.value = await res.json();
		} else {
			auditLogsSignal.value = [];
		}
	} catch (e) {
		console.error('Failed to fetch audit logs', e);
	}
}

function renderJSONDiff(oldRowStr: string | null, newRowStr: string | null): string {
	let oldObj: any = {};
	let newObj: any = {};

	try {
		if (oldRowStr) {
			oldObj = typeof oldRowStr === 'string' ? JSON.parse(oldRowStr) : oldRowStr;
		}
	} catch (e) {
		console.error('Failed to parse oldRow JSON', e);
	}

	try {
		if (newRowStr) {
			newObj = typeof newRowStr === 'string' ? JSON.parse(newRowStr) : newRowStr;
		}
	} catch (e) {
		console.error('Failed to parse newRow JSON', e);
	}

	const allKeys = Array.from(new Set([...Object.keys(oldObj), ...Object.keys(newObj)])).sort();

	if (allKeys.length === 0) {
		return `
			<tr>
				<td colspan="3" style="padding: 1rem; text-align: center; color: var(--p-color-text-subdued);">No row attributes captured in this log.</td>
			</tr>
		`;
	}

	return allKeys.map(key => {
		const oldVal = oldObj[key];
		const newVal = newObj[key];

		const existsInOld = key in oldObj;
		const existsInNew = key in newObj;

		const oldValStr = existsInOld ? (typeof oldVal === 'object' ? JSON.stringify(oldVal) : String(oldVal)) : '';
		const newValStr = existsInNew ? (typeof newVal === 'object' ? JSON.stringify(newVal) : String(newVal)) : '';

		if (existsInOld && existsInNew) {
			if (oldValStr !== newValStr) {
				return `
					<tr style="border-bottom: 1px solid var(--p-color-border-subdued);">
						<td style="padding: 0.5rem; font-weight: bold; border-right: 1px solid var(--p-color-border-subdued); vertical-align: top;">${key}</td>
						<td style="padding: 0.5rem; background: #ffebe9; color: #8a1f11; border-right: 1px solid var(--p-color-border-subdued); word-break: break-all; vertical-align: top;">${oldValStr}</td>
						<td style="padding: 0.5rem; background: #e6ffed; color: #1a7f37; word-break: break-all; vertical-align: top;">${newValStr}</td>
					</tr>
				`;
			} else {
				return `
					<tr style="border-bottom: 1px solid var(--p-color-border-subdued); opacity: 0.6;">
						<td style="padding: 0.5rem; font-weight: bold; border-right: 1px solid var(--p-color-border-subdued); vertical-align: top;">${key}</td>
						<td style="padding: 0.5rem; border-right: 1px solid var(--p-color-border-subdued); word-break: break-all; vertical-align: top;">${oldValStr}</td>
						<td style="padding: 0.5rem; word-break: break-all; vertical-align: top;">${newValStr}</td>
					</tr>
				`;
			}
		} else if (existsInNew) {
			return `
				<tr style="border-bottom: 1px solid var(--p-color-border-subdued);">
					<td style="padding: 0.5rem; font-weight: bold; border-right: 1px solid var(--p-color-border-subdued); vertical-align: top;">${key}</td>
					<td style="padding: 0.5rem; border-right: 1px solid var(--p-color-border-subdued); background: var(--p-color-bg-surface-secondary); vertical-align: top;"></td>
					<td style="padding: 0.5rem; background: #e6ffed; color: #1a7f37; word-break: break-all; vertical-align: top;">${newValStr}</td>
				</tr>
			`;
		} else {
			return `
				<tr style="border-bottom: 1px solid var(--p-color-border-subdued);">
					<td style="padding: 0.5rem; font-weight: bold; border-right: 1px solid var(--p-color-border-subdued); vertical-align: top;">${key}</td>
					<td style="padding: 0.5rem; background: #ffebe9; color: #8a1f11; border-right: 1px solid var(--p-color-border-subdued); word-break: break-all; vertical-align: top;">${oldValStr}</td>
					<td style="padding: 0.5rem; background: var(--p-color-bg-surface-secondary); vertical-align: top;"></td>
				</tr>
			`;
		}
	}).join('');
}

export function renderAuditPage(): string {
	const role = activeRoleSignal.value;
	const isAuthorized = role === 'superadmin' || role === 'admin' || role === 'manager';

	if (!isAuthorized) {
		return `
			<s-page heading="Access Denied" inlineSize="large">
				<s-section>
					<s-box padding="base" background="surface" border="base" borderRadius="base">
						<s-banner tone="critical">
							You do not have permissions to access the Audit Trail Visualizer. Contact your administrator if this is an error.
						</s-banner>
					</s-box>
				</s-section>
			</s-page>
		`;
	}

	const logs = auditLogsSignal.value || [];
	const filters = auditFiltersSignal.value;

	const tableRowsHtml = logs.map(a => {
		let badgeTone = '';
		if (a.action === 'INSERT') badgeTone = 'success';
		else if (a.action === 'UPDATE') badgeTone = 'attention';
		else if (a.action === 'DELETE') badgeTone = 'critical';

		const dateStr = new Date(a.createdAt).toLocaleString();
		const actorEmail = usersMapSignal.value.get(a.actorId) || a.actorId || 'System';

		return `
			<s-table-row>
				<s-table-cell>${dateStr}</s-table-cell>
				<s-table-cell><span style="font-size:0.9rem;">${actorEmail}</span></s-table-cell>
				<s-table-cell><s-badge ${badgeTone ? `tone="${badgeTone}"` : ''}>${a.action}</s-badge></s-table-cell>
				<s-table-cell><strong>${a.tableName}</strong></s-table-cell>
				<s-table-cell><span style="font-family:monospace; font-size:0.85rem;">${a.recordId}</span></s-table-cell>
				<s-table-cell>
					<s-button id="btn-inspect-audit-${a.id}" size="slim">Inspect</s-button>
				</s-table-cell>
			</s-table-row>
		`;
	}).join('');

	// Pagination buttons state
	const hasPrevious = filters.offset > 0;
	const hasNext = logs.length === filters.limit;

	return `
		<s-page heading="Audit Trail Visualizer" subtitle="Track transactional history, review diff modifications, and trigger database rollbacks." inlineSize="large">
			<!-- Query Filter Panel -->
			<s-section>
				<s-box padding="base" background="surface" border="base" borderRadius="base">
					<s-stack gap="base">
						<s-stack direction="inline" gap="base" wrap>
							<div style="flex: 1; min-width: 180px;">
								<label style="display:block; font-size:0.875rem; font-weight:500; margin-bottom:0.25rem;">Entity Group</label>
								<select id="filter-audit-table" class="polaris-select-custom" style="width:100%; padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
									<option value="all" ${filters.tableName === 'all' ? 'selected' : ''}>All Tables</option>
									<option value="assets" ${filters.tableName === 'assets' ? 'selected' : ''}>Assets</option>
									<option value="contracts" ${filters.tableName === 'contracts' ? 'selected' : ''}>Contracts</option>
									<option value="tickets" ${filters.tableName === 'tickets' ? 'selected' : ''}>Tickets</option>
									<option value="users" ${filters.tableName === 'users' ? 'selected' : ''}>Users</option>
								</select>
							</div>
							<div style="flex: 1; min-width: 180px;">
								<label style="display:block; font-size:0.875rem; font-weight:500; margin-bottom:0.25rem;">Operation</label>
								<select id="filter-audit-action" class="polaris-select-custom" style="width:100%; padding:0.5rem; border:1px solid var(--p-color-border-subdued); border-radius:4px; background:var(--p-color-bg-surface); height:38px;">
									<option value="all" ${filters.action === 'all' ? 'selected' : ''}>All Operations</option>
									<option value="INSERT" ${filters.action === 'INSERT' ? 'selected' : ''}>INSERT</option>
									<option value="UPDATE" ${filters.action === 'UPDATE' ? 'selected' : ''}>UPDATE</option>
									<option value="DELETE" ${filters.action === 'DELETE' ? 'selected' : ''}>DELETE</option>
								</select>
							</div>
						</s-stack>
					</s-stack>
				</s-box>
			</s-section>

			<!-- Logs Timeline Section -->
			<s-section>
				<s-box padding="none" background="surface" border="base" borderRadius="base">
					${logs.length === 0 ? `
						<div style="padding: 3rem; text-align: center;">
							<s-text tone="subdued">No audit logs matching selection filters.</s-text>
						</div>
					` : `
						<s-table>
							<s-table-header-row>
								<s-table-header>Timestamp</s-table-header>
								<s-table-header>Actor ID</s-table-header>
								<s-table-header>Action</s-table-header>
								<s-table-header>Table Name</s-table-header>
								<s-table-header>Record ID</s-table-header>
								<s-table-header>Actions</s-table-header>
							</s-table-header-row>
							<s-table-body>
								${tableRowsHtml}
							</s-table-body>
						</s-table>
					`}
				</s-box>
				
				<!-- Pagination Controls -->
				${logs.length > 0 || hasPrevious ? `
					<div style="display: flex; justify-content: flex-end; padding: 1rem 0; gap: 0.5rem;">
						<s-button id="btn-audit-prev" ${!hasPrevious ? 'disabled' : ''}>Previous</s-button>
						<s-button id="btn-audit-next" ${!hasNext ? 'disabled' : ''}>Next</s-button>
					</div>
				` : ''}
			</s-section>

			<!-- Audit Log Inspection Modal -->
			<s-modal id="audit-detail-modal" heading="Audit Record Detail Inspection">
				<s-stack gap="base">
					<div style="border-bottom: 1px solid var(--p-color-border-subdued); padding-bottom: 0.75rem;">
						<s-stack gap="none">
							<s-text><strong>Entity Group:</strong> <span id="diff-entity-type"></span></s-text>
							<s-text><strong>Record UUID:</strong> <span id="diff-entity-id"></span></s-text>
							<s-text><strong>Executed By:</strong> <span id="diff-actor-id"></span></s-text>
							<s-text><strong>Timestamp:</strong> <span id="diff-timestamp"></span></s-text>
						</s-stack>
					</div>

					<s-text>Compare field changes (old_row left vs new_row right):</s-text>

					<!-- Side-by-side JSON Diff Viewer -->
					<div class="diff-viewer-wrapper" style="width: 100%; overflow-x: auto; border: 1px solid var(--p-color-border-subdued); border-radius: 6px;">
						<table style="width: 100%; border-collapse: collapse; font-family: monospace; font-size: 0.85rem; background: var(--p-color-bg-surface);">
							<thead>
								<tr style="background: var(--p-color-bg-surface-secondary); border-bottom: 1px solid var(--p-color-border-subdued);">
									<th style="padding: 0.5rem; text-align: left; width: 30%; border-right: 1px solid var(--p-color-border-subdued);">Field Name</th>
									<th style="padding: 0.5rem; text-align: left; width: 35%; border-right: 1px solid var(--p-color-border-subdued);">Previous Value (old_row)</th>
									<th style="padding: 0.5rem; text-align: left; width: 35%;">New Value (new_row)</th>
								</tr>
							</thead>
							<tbody id="diff-table-body">
								<!-- Rendered dynamically -->
							</tbody>
						</table>
					</div>

					<!-- Rollback warnings -->
					<div id="rollback-warning-banner" style="display: none;">
						<s-banner tone="critical">
							Reversing this action will enqueue a background River task executing database reversion scripts. Conflicting updates made after this entry may reject the reversal.
						</s-banner>
					</div>

					<div id="rollback-error-banner" style="display: none; color: var(--p-color-text-critical); font-weight: 500; font-size: 0.875rem;"></div>

					<div style="display: flex; justify-content: flex-end; gap: 0.5rem; margin-top: 1.5rem;" id="audit-modal-actions">
						<!-- Custom actions dynamically injected -->
					</div>
				</s-stack>
			</s-modal>
		</s-page>
	`;
}

export function setupAuditPageListeners() {
	const role = activeRoleSignal.value;
	const isReadOnly = role === 'manager';

	// 1. Table Name Filter listener
	const tableSelect = document.getElementById('filter-audit-table');
	if (tableSelect) {
		tableSelect.addEventListener('change', async (e: any) => {
			auditFiltersSignal.value = {
				...auditFiltersSignal.value,
				tableName: e.target.value,
				offset: 0, // reset offset on filter change
			};
			await refreshAuditLogs();
			triggerAppReRender();
		});
	}

	// 2. Action Filter listener
	const actionSelect = document.getElementById('filter-audit-action');
	if (actionSelect) {
		actionSelect.addEventListener('change', async (e: any) => {
			auditFiltersSignal.value = {
				...auditFiltersSignal.value,
				action: e.target.value,
				offset: 0,
			};
			await refreshAuditLogs();
			triggerAppReRender();
		});
	}

	// 3. Pagination click handlers
	const prevBtn = document.getElementById('btn-audit-prev');
	if (prevBtn) {
		prevBtn.addEventListener('click', async () => {
			const currentFilters = auditFiltersSignal.value;
			let newOffset = currentFilters.offset - currentFilters.limit;
			if (newOffset < 0) newOffset = 0;

			auditFiltersSignal.value = {
				...currentFilters,
				offset: newOffset,
			};
			await refreshAuditLogs();
			triggerAppReRender();
		});
	}

	const nextBtn = document.getElementById('btn-audit-next');
	if (nextBtn) {
		nextBtn.addEventListener('click', async () => {
			const currentFilters = auditFiltersSignal.value;
			const newOffset = currentFilters.offset + currentFilters.limit;

			auditFiltersSignal.value = {
				...currentFilters,
				offset: newOffset,
			};
			await refreshAuditLogs();
			triggerAppReRender();
		});
	}

	// 4. Attach Row Inspect click listeners
	const logs = auditLogsSignal.value || [];
	logs.forEach(a => {
		const inspectBtn = document.getElementById(`btn-inspect-audit-${a.id}`);
		if (inspectBtn) {
			inspectBtn.addEventListener('click', () => {
				selectedAuditLogSignal.value = a;

				const typeEl = document.getElementById('diff-entity-type');
				if (typeEl) typeEl.textContent = a.tableName;

				const uuidEl = document.getElementById('diff-entity-id');
				if (uuidEl) uuidEl.textContent = a.recordId;

				const actorEl = document.getElementById('diff-actor-id');
				if (actorEl) actorEl.textContent = usersMapSignal.value.get(a.actorId) || a.actorId || 'System / Automated';

				const stampEl = document.getElementById('diff-timestamp');
				if (stampEl) stampEl.textContent = new Date(a.createdAt).toLocaleString();

				const diffBody = document.getElementById('diff-table-body');
				if (diffBody) {
					diffBody.innerHTML = renderJSONDiff(a.oldRow, a.newRow);
				}

				const warningEl = document.getElementById('rollback-warning-banner');
				if (warningEl) {
					warningEl.style.display = isReadOnly ? 'none' : 'block';
				}

				const errEl = document.getElementById('rollback-error-banner');
				if (errEl) errEl.style.display = 'none';

				// Set primary actions based on permissions
				const actionsContainer = document.getElementById('audit-modal-actions');
				if (actionsContainer) {
					if (isReadOnly) {
						actionsContainer.innerHTML = `
							<s-text tone="subdued" style="margin-right: auto; align-self: center; font-size: 0.85rem;">Reversal actions restricted to administrative roles.</s-text>
							<s-button variant="primary" commandFor="audit-detail-modal" command="--hide">Close</s-button>
						`;
					} else {
						const isLoading = rollbackLoadingSignal.value;
						actionsContainer.innerHTML = `
							<s-button variant="primary" tone="critical" id="btn-revert-action" ${isLoading ? 'loading="true" disabled' : ''}>Reverse Action</s-button>
							<s-button commandFor="audit-detail-modal" command="--hide" ${isLoading ? 'disabled' : ''}>Cancel</s-button>
						`;

						// Bind the rollback action listener
						const revertBtn = document.getElementById('btn-revert-action');
						if (revertBtn) {
							revertBtn.addEventListener('click', async () => {
								await executeRollback(a.id);
							});
						}
					}
				}

				const modal = document.getElementById('audit-detail-modal') as any;
				if (modal && typeof modal.showOverlay === 'function') {
					modal.showOverlay();
				}
			});
		}
	});
}

async function executeRollback(auditLogId: string) {
	const errEl = document.getElementById('rollback-error-banner');
	if (errEl) errEl.style.display = 'none';

	rollbackLoadingSignal.value = true;
	updateModalActionButtons(true);

	try {
		const res = await fetch(`/api/v1/audit-logs/${auditLogId}/rollback`, {
			method: 'POST',
			headers: getAuthHeaders(),
		});

		if (res.ok) {
			const modal = document.getElementById('audit-detail-modal') as any;
			if (modal && typeof modal.hideOverlay === 'function') {
				modal.hideOverlay();
			}
			await refreshAuditLogs();
			triggerAppReRender();
		} else {
			const data = await res.json();
			if (errEl) {
				errEl.textContent = data.error || 'Failed to execute database reversal.';
				errEl.style.display = 'block';
			}
		}
	} catch (err) {
		if (errEl) {
			errEl.textContent = 'Connection failure during database rollback request.';
			errEl.style.display = 'block';
		}
	} finally {
		rollbackLoadingSignal.value = false;
		updateModalActionButtons(false);
	}
}

function updateModalActionButtons(isLoading: boolean) {
	const revertBtn = document.getElementById('btn-revert-action') as any;
	if (revertBtn) {
		if (isLoading) {
			revertBtn.setAttribute('loading', 'true');
			revertBtn.setAttribute('disabled', 'true');
		} else {
			revertBtn.removeAttribute('loading');
			revertBtn.removeAttribute('disabled');
		}
	}
}

function triggerAppReRender() {
	const ev = new HashChangeEvent('hashchange');
	window.dispatchEvent(ev);
}
